<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>カスタマーランキング</span>
      <div class="d-flex align-center">
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 120px;"
          class="mr-2"
          @change="handleTypeChange"
        />
        <v-select
          v-model="selectedLimit"
          :items="limitOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 100px;"
          class="mr-2"
          @change="handleLimitChange"
        />
        <v-btn
          :disabled="isLoading || ranking.length === 0"
          :loading="downloadingCsv"
          color="secondary"
          small
          outlined
          @click="downloadCsv"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiDownload }}
          </v-icon>
          CSV
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <v-simple-table
        v-else-if="ranking.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th
                class="text-center"
                style="width: 64px; white-space: nowrap;"
              >
                順位
              </th>
              <th>顧客名</th>
              <th class="text-right">
                大計売上
              </th>
              <th class="text-right">
                来店回数
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in ranking"
              :key="item.customerId"
              class="cursor-pointer"
              @click="handleRowClick(item)"
            >
              <td class="text-center">
                <v-chip
                  v-if="item.rank <= 3"
                  :color="getRankColor(item.rank)"
                  small
                  text-color="white"
                >
                  {{ item.rank }}
                </v-chip>
                <span v-else>{{ item.rank }}</span>
              </td>
              <td>{{ item.customerName }}</td>
              <td class="text-right">
                {{ formatCurrency(item.totalAmountWithTax) }}
              </td>
              <td class="text-right text-caption text--secondary">
                {{ item.visitCount }}回
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div
        v-else
        class="text-center py-8 grey--text"
      >
        データがありません
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref, watch, computed,
} from '@vue/composition-api'
import { mdiDownload } from '@mdi/js'
import { formatCurrency } from '@/utils/formatters'
import { downloadBlob } from '@/utils/csv'
import useCustomerRanking, { RANKING_TYPES, LIMIT_OPTIONS } from '../composables/useCustomerRanking'

// 日付を YYYY-MM-DD 文字列に変換（Date / 文字列どちらも対応）
const toDateStr = date => {
  if (!date) return ''
  if (typeof date === 'string') return date.slice(0, 10)

  return date.toISOString().slice(0, 10)
}

export default {
  name: 'CustomerRanking',
  props: {
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const {
      ranking,
      loading,
      downloadingCsv,
      fetchRanking,
      fetchRankingCsv,
    } = useCustomerRanking(root)

    const selectedType = ref('sales')
    const selectedLimit = ref(10)

    const isLoading = computed(() => loading.value)

    const typeOptions = RANKING_TYPES
    const limitOptions = LIMIT_OPTIONS

    const getRankColor = rank => {
      if (rank === 1) return '#D4A017'
      if (rank === 2) return '#7B8794'
      if (rank === 3) return '#A0522D'

      return 'grey'
    }

    const loadRanking = () => {
      fetchRanking({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
      })
    }

    const handleTypeChange = () => {
      loadRanking()
    }

    const handleLimitChange = () => {
      loadRanking()
    }

    const handleRowClick = item => {
      emit('customer-click', item.customerId)
    }

    // ランキングCSV（大計＝リアルトータル・合計行付き）をAPIから取得してダウンロードする
    const downloadCsv = async () => {
      const blob = await fetchRankingCsv({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
      })
      if (!blob) return

      const period = [toDateStr(props.startDate), toDateStr(props.endDate)].filter(Boolean).join('_')
      const fileName = `customer_ranking${period ? `_${period}` : ''}.csv`

      downloadBlob({ blob, fileName })
    }

    // 日付が変更されたらリロード
    watch(
      () => [props.startDate, props.endDate],
      () => {
        loadRanking()
      },
      { immediate: true },
    )

    return {
      ranking,
      isLoading,
      downloadingCsv,
      selectedType,
      selectedLimit,
      typeOptions,
      limitOptions,
      formatCurrency,
      getRankColor,
      handleTypeChange,
      handleLimitChange,
      handleRowClick,
      downloadCsv,
      icons: { mdiDownload },
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
